<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <NotificationBox v-if="blockErrorMessage" alertType="warning">
        {{ blockErrorMessage }}
    </NotificationBox>

    <NotificationBox v-if="errorMessage" alertType="warning">
        {{ errorMessage }}
    </NotificationBox>

    <NotificationBox v-if="successMessage" alertType="success">
        {{ successMessage }}
    </NotificationBox>

    <div v-if="shouldShowAttendanceForm">
        <div ref="headerElement"></div>
        <RockForm @submit="onSubmit">
            <CheckboxList v-model="selectedAttendees" label="Attendees" :items="attendees" @update:modelValue="onAttendeesUpdated" />

            <div class="actions">
                <RockButton type="submit" :btnType="BtnType.Primary" :disabled="isSaving">Save</RockButton>
            </div>
        </RockForm>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import NotificationBox from "@Obsidian/Controls/notificationBox.obs";
    import CheckboxList from "@Obsidian/Controls/checkBoxList";
    import RockButton from "@Obsidian/Controls/rockButton";
    import RockForm from "@Obsidian/Controls/rockForm";
    import { BtnType } from "@Obsidian/Enums/Controls/btnType";
    import { HttpResult } from "@Obsidian/Types/Utility/http";
    import { useConfigurationValues, useReloadBlock, onConfigurationValuesChanged, useInvokeBlockAction } from "@Obsidian/Utility/block";
    import { SignUpAttendanceBag } from "@Obsidian/ViewModels/Blocks/Engagement/SignUp/SignUpAttendanceDetail/signUpAttendanceBag";
    import { SignUpAttendanceDetailInitializationBox } from "@Obsidian/ViewModels/Blocks/Engagement/SignUp/SignUpAttendanceDetail/signUpAttendanceDetailInitializationBox";
    import { SignUpAttendeeBag } from "@Obsidian/ViewModels/Blocks/Engagement/SignUp/SignUpAttendanceDetail/signUpAttendeeBag";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";

    const config = useConfigurationValues<SignUpAttendanceDetailInitializationBox>();
    const invokeBlockAction = useInvokeBlockAction();

    // #region Values

    const headerElement = ref<HTMLElement | null>(null);
    const selectedAttendees = ref<string[]>([]);

    const errorMessage = ref("");
    const successMessage = ref("");
    const isSaving = ref(false);

    // #endregion

    // #region Computed Values

    const blockErrorMessage = computed((): string | undefined | null => {
        return config.errorMessage;
    });

    const attendees = computed((): ListItemBag[] => {
        var listItemBag: ListItemBag[] = config.attendees?.map(a => ({
            text: a.name,
            value: a.personAliasId.toString()
        })) ?? [];

        return listItemBag;
    });

    const shouldShowAttendanceForm = computed((): boolean => {
        if (blockErrorMessage.value) {
            return false;
        }

        return !successMessage.value;
    });

    // #endregion

    // #region Event Handlers

    /**
     * Handles the `update:modelValue` event of the Attendees `CheckBoxList`.
     */
    function onAttendeesUpdated(): void {
        if (!config.attendees?.length) {
            return;
        }

        config.attendees.forEach((a: SignUpAttendeeBag) => a.didAttend = selectedAttendees.value.includes(a.personAliasId.toString()));
    }

    /**
     * Handles the form submission.
     */
    async function onSubmit(): Promise<void> {
        isSaving.value = true;
        errorMessage.value = "";
        successMessage.value = "";

        const bag: SignUpAttendanceBag = {
            attendees: config.attendees
        };

        const result: HttpResult<void> = await invokeBlockAction("SaveAttendance", { bag });
        isSaving.value = false;

        if (!result.isSuccess) {
            errorMessage.value = result.errorMessage ?? "Unknown error when trying to enter attendance.";
            return;
        }

        successMessage.value = "Successfully entered attendance.";
    }

    // #endregion

    onConfigurationValuesChanged(useReloadBlock());

    if (config.attendees?.length) {
        config.attendees
            .filter((a: SignUpAttendeeBag) => a.didAttend)
            .forEach((a: SignUpAttendeeBag) => selectedAttendees.value.push(a.personAliasId.toString()));
    }

    onMounted(() => {
        if (headerElement.value && config.headerHtml) {
            headerElement.value.innerHTML = config.headerHtml;
        }
    });
</script>
